import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PagedList } from '../models/paged-list.interface';
import { RequestModel } from '../models/request-model.interface';
import { CompanySalesOrderCreate } from '../models/sales/company-sales-order-create.interface';
import { CompanySalesOrderDetails } from '../models/sales/company-sales-order-details.interface';
import { CompanySalesOrderLineUpsert } from '../models/sales/company-sales-order-line-upsert.interface';
import { CompanySalesOrderOverview } from '../models/sales/company-sales-order-overview.interface';
import { CompanySalesOrderUpdate } from '../models/sales/company-sales-order-update.interface';
import { SalesOrderChange } from '../models/sales/sales-order-change.interface';

@Injectable({
  providedIn: 'root',
})
export class SalesOrderService {
  private readonly httpClient = inject(HttpClient);


  public getSalesOrders(companyId: string): Observable<CompanySalesOrderOverview[]> {
    return this.httpClient.get<CompanySalesOrderOverview[]>(this.getSalesOrdersUrl(companyId));
  }

  public getSalesOrdersPaged(
    companyId: string,
    request: RequestModel,
  ): Observable<PagedList<CompanySalesOrderOverview>> {
    return this.httpClient.post<PagedList<CompanySalesOrderOverview>>(
      `${this.getSalesOrdersUrl(companyId)}/company-sales-orders`,
      request,
    );
  }

  public getSalesOrder(companyId: string, salesOrderId: string): Observable<CompanySalesOrderDetails> {
    return this.httpClient.get<CompanySalesOrderDetails>(`${this.getSalesOrdersUrl(companyId)}/${salesOrderId}`);
  }

  public createSalesOrder(
    companyId: string,
    payload: CompanySalesOrderCreate = {},
  ): Observable<CompanySalesOrderDetails> {
    return this.httpClient.post<CompanySalesOrderDetails>(this.getSalesOrdersUrl(companyId), payload);
  }

  public updateSalesOrder(
    companyId: string,
    salesOrderId: string,
    payload: CompanySalesOrderUpdate,
  ): Observable<CompanySalesOrderDetails> {
    return this.httpClient.patch<CompanySalesOrderDetails>(`${this.getSalesOrdersUrl(companyId)}/${salesOrderId}`, payload);
  }

  public createSalesOrderLine(
    companyId: string,
    salesOrderId: string,
    payload: CompanySalesOrderLineUpsert,
  ): Observable<CompanySalesOrderDetails> {
    return this.httpClient.post<CompanySalesOrderDetails>(
      `${this.getSalesOrdersUrl(companyId)}/${salesOrderId}/lines`,
      payload,
    );
  }

  public updateSalesOrderLine(
    companyId: string,
    salesOrderId: string,
    lineId: string,
    payload: CompanySalesOrderLineUpsert,
  ): Observable<CompanySalesOrderDetails> {
    return this.httpClient.patch<CompanySalesOrderDetails>(
      `${this.getSalesOrdersUrl(companyId)}/${salesOrderId}/lines/${lineId}`,
      payload,
    );
  }

  public deleteSalesOrderLine(companyId: string, salesOrderId: string, lineId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.getSalesOrdersUrl(companyId)}/${salesOrderId}/lines/${lineId}`);
  }

  public archiveSalesOrder(companyId: string, salesOrderId: string): Observable<CompanySalesOrderDetails> {
    return this.httpClient.post<CompanySalesOrderDetails>(
      `${this.getSalesOrdersUrl(companyId)}/${salesOrderId}/archive`,
      null,
    );
  }

  public unarchiveSalesOrder(companyId: string, salesOrderId: string): Observable<CompanySalesOrderDetails> {
    return this.httpClient.post<CompanySalesOrderDetails>(
      `${this.getSalesOrdersUrl(companyId)}/${salesOrderId}/unarchive`,
      null,
    );
  }

  public deleteSalesOrder(companyId: string, salesOrderId: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.getSalesOrdersUrl(companyId)}/${salesOrderId}`,
    );
  }

  public duplicateSalesOrder(companyId: string, salesOrderId: string): Observable<CompanySalesOrderDetails> {
    return this.httpClient.post<CompanySalesOrderDetails>(
      `${this.getSalesOrdersUrl(companyId)}/${salesOrderId}/duplicate`,
      {},
    );
  }

  public getSalesOrderChanges(companyId: string, salesOrderId: string): Observable<SalesOrderChange[]> {
    return this.httpClient.get<SalesOrderChange[]>(
      `${this.getSalesOrdersUrl(companyId)}/${salesOrderId}/changes`,
    );
  }

  private getSalesOrdersUrl(companyId: string): string {
    return `${environment.apiUrl}/companies/${companyId}/sales-orders`;
  }
}
